import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import TopBar from '../components/TopBar';
import MetaLabel from '../components/MetaLabel';
import ChatView from '../components/ChatView';
import { tokens } from '../tokens';
import useIsDesktop from '../useIsDesktop';
import api from '../api';
import { generateKeyPair, storeKeyPair } from '../utils/crypto';

export default function GhostHours() {
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const [availability, setAvailability] = useState(null);
  const [matches, setMatches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/ghost-hours/availability'),
      api.get('/ghost-hours/rooms')
    ]).then(([availRes, roomsRes]) => {
      setAvailability(availRes.data.availability);
      setNote(availRes.data.availability?.note || '');
      setRooms(roomsRes.data.rooms);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleAvailability = async () => {
    const newAvail = !availability?.available;
    const { data } = await api.post('/ghost-hours/availability', { available: newAvail, note });
    setAvailability(data.availability);
    if (newAvail) fetchMatches();
  };

  const updateNote = async () => {
    const { data } = await api.post('/ghost-hours/availability', { available: availability?.available ?? true, note });
    setAvailability(data.availability);
  };

  const fetchMatches = useCallback(async () => {
    const { data } = await api.get('/ghost-hours/matches');
    setMatches(data.matches);
  }, []);

  useEffect(() => {
    if (availability?.available) fetchMatches();
  }, [availability?.available, fetchMatches]);

  const startChat = async (peerId) => {
    const keyPair = generateKeyPair();
    const { data } = await api.post('/ghost-hours/rooms', { peerId, publicKey: keyPair.publicKey });
    storeKeyPair(data.room._id, keyPair);
    setRooms(prev => [data.room, ...prev.filter(r => r._id !== data.room._id)]);
    setActiveRoom(data.room._id);
  };

  // When activeRoom is set, render ChatView without Navbar wrapper
  if (activeRoom) {
    return (
      <ChatView
        roomId={activeRoom}
        onBack={() => {
          setActiveRoom(null);
          api.get('/ghost-hours/rooms').then(r => setRooms(r.data.rooms));
        }}
      />
    );
  }

  const isAvailable = availability?.available;

  // Shared Your Light card
  const lightCard = (large) => (
    <div style={{
      border: `1px solid ${isAvailable ? tokens.accent + '55' : tokens.edge}`,
      background: isAvailable ? tokens.accentGlow : 'transparent',
      padding: large ? '22px 22px' : '16px 18px',
      transition: `all 300ms ${tokens.ease}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: large ? 14 : 12 }}>
          <span style={{
            width: large ? 11 : 10, height: large ? 11 : 10, borderRadius: '50%',
            background: isAvailable ? tokens.calm : tokens.textDim,
            boxShadow: isAvailable ? `0 0 14px ${tokens.calm}` : 'none',
            animation: isAvailable ? 'gl-breathe 3s ease-in-out infinite' : 'none',
            display: 'inline-block',
          }} />
          <div>
            <div style={{
              fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: tokens.textLow, marginBottom: 2,
            }}>Your Light</div>
            <div style={{
              fontFamily: tokens.fontDisplay, fontSize: large ? 17 : 16, color: tokens.textHi,
            }}>{isAvailable ? 'On \u2014 open to talk' : 'Off \u2014 not right now'}</div>
          </div>
        </div>
        <button className="gl-btn-bare" onClick={toggleAvailability}
          role="switch" aria-checked={!!isAvailable} aria-label="Toggle availability"
          style={{
            width: 44, height: 24, borderRadius: 12,
            background: isAvailable ? tokens.accent : tokens.edge,
            position: 'relative', transition: `all 240ms ${tokens.ease}`,
          }}>
          <span style={{
            position: 'absolute', top: 2, left: isAvailable ? 22 : 2,
            width: 20, height: 20, borderRadius: '50%',
            background: tokens.ground, transition: `left 240ms ${tokens.ease}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }} />
        </button>
      </div>
      {isAvailable && (
        <input value={note} onChange={e => setNote(e.target.value)} onBlur={updateNote}
          placeholder="what's on your mind (peers will see)" maxLength={200}
          style={{
            marginTop: large ? 16 : 14, width: '100%', background: 'transparent',
            border: 'none', borderBottom: `1px solid ${tokens.edge}`,
            color: tokens.textMid, fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 15, padding: large ? '8px 0' : '6px 0', outline: 'none',
          }}
        />
      )}
      {large && (
        <>
          <div style={{
            marginTop: 24, fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: tokens.textLow, marginBottom: 8,
          }}>How this works</div>
          <div style={{ fontFamily: tokens.fontBody, fontSize: 13, color: tokens.textMid, lineHeight: 1.6 }}>
            End-to-end encrypted. Ephemeral &mdash; messages vanish when you close the tab. We never see your words.
          </div>
        </>
      )}
    </div>
  );

  // Shared peer list
  const peerListJSX = (
    <>
      {loading ? (
        <p style={{ fontFamily: tokens.fontDisplay, fontStyle: 'italic', fontSize: 15, color: tokens.textMid }}>Finding your people...</p>
      ) : !isAvailable ? (
        <p style={{ fontFamily: tokens.fontBody, fontSize: 15, color: tokens.textMid, lineHeight: 1.6 }}>Toggle your light to see who else is here.</p>
      ) : matches.length === 0 ? (
        <>
          <MetaLabel style={{ marginBottom: 16 }}>Here right now &middot; 0</MetaLabel>
          <p style={{ fontFamily: tokens.fontBody, fontSize: 15, color: tokens.textMid, lineHeight: 1.6 }}>No one else is here right now &mdash; but your light is on.</p>
          <p style={{ fontFamily: tokens.fontBody, fontSize: 13, color: tokens.textLow, marginTop: 8 }}>Others will see you when they arrive.</p>
        </>
      ) : (
        <>
          <div style={{ fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: tokens.accent, marginBottom: 16 }}>Here right now &middot; {matches.length}</div>
          {matches.map((m, i) => (
            <button key={m.id} className="gl-btn-bare" onClick={() => startChat(m.id)} style={{
              display: 'block', width: '100%', padding: '20px 0', borderBottom: `1px solid ${tokens.edge}`,
              animation: `gl-fade-up 500ms ${tokens.ease} ${i * 80}ms both`,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px auto', alignItems: 'baseline', gap: 18 }}>
                <span style={{ fontFamily: tokens.fontDisplay, fontSize: 21, color: tokens.textHi, letterSpacing: tokens.displayTracking, textAlign: 'left' }}>{m.alias}</span>
                <span style={{ fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: tokens.textLow }}>{m.department}</span>
                <span style={{ fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em', color: tokens.accent }}>Connect →</span>
              </div>
              {m.note && (
                <div style={{ fontFamily: tokens.fontDisplay, fontStyle: 'italic', fontSize: 14, color: tokens.textMid, marginTop: 8, textAlign: 'left' }}>"{m.note}"</div>
              )}
            </button>
          ))}
        </>
      )}
    </>
  );

  /* ── Desktop: two-column ── */
  if (isDesktop) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column',
        background: tokens.ground, color: tokens.textHi, fontFamily: tokens.fontBody, position: 'relative' }}>
        <div className="gl-atmosphere" />
        <Navbar />

        <main style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: '48px 40px 80px' }}>
            <h1 style={{
              fontFamily: tokens.fontDisplay, fontSize: 44, lineHeight: 1.1,
              fontWeight: tokens.displayWeight, letterSpacing: tokens.displayTracking,
              color: tokens.textHi, margin: 0,
            }}>Talk to someone who gets it.</h1>
            <p style={{
              fontFamily: tokens.fontDisplay, fontStyle: 'italic',
              fontSize: 17, color: tokens.textMid, marginTop: 12, lineHeight: 1.5,
            }}>Not therapy. Just someone who's done load-out at 3am.</p>

            <div style={{
              marginTop: 48,
              display: 'grid', gridTemplateColumns: '380px 1fr', gap: 60,
              alignItems: 'start',
            }}>
              {lightCard(true)}
              <div>{peerListJSX}</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ── Mobile: single column ── */
  return (
    <div style={{ position: 'relative', minHeight: '100vh', paddingBottom: 120 }}>
      <Navbar />
      <div className="gl-atmosphere" />

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <TopBar title="Ghost Hours" amberTitle />

      <div style={{ padding: '20px 22px 0', position: 'relative', zIndex: 2 }}>
        <h1 style={{
          fontFamily: tokens.fontDisplay, fontSize: 30, lineHeight: 1.15,
          fontWeight: tokens.displayWeight, letterSpacing: tokens.displayTracking,
          color: tokens.textHi, margin: 0,
        }}>Talk to someone{'\n'}who gets it.</h1>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 15, color: tokens.textMid, marginTop: 10, lineHeight: 1.5,
        }}>Not therapy. Just someone who's done load-out at 3am.</p>
      </div>

      <div style={{ padding: '24px 22px 0', position: 'relative', zIndex: 2 }}>
        {lightCard(false)}
      </div>

      <div style={{ padding: '36px 22px 0', position: 'relative', zIndex: 2 }}>
        {peerListJSX}
      </div>

      <div style={{ padding: '48px 22px 0', position: 'relative', zIndex: 2 }}>
        <div style={{ fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: tokens.textLow, marginBottom: 8 }}>How this works</div>
        <div style={{ fontFamily: tokens.fontBody, fontSize: 13, color: tokens.textMid, lineHeight: 1.7 }}>End-to-end encrypted. Ephemeral &mdash; messages vanish when you close the tab. We never see your words.</div>
      </div>
      </div>
    </div>
  );
}
